import { useEffect, useMemo, useRef, useState } from "react";

import { Email } from "../../../factories/emails/models/emails.models";
import { WorkModel } from "../../../factories/works/models/work.model";
import { useAppSelector } from "../../../redux/redux";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useLazyGetCustomersByEmailQuery } from "../../../slices/ContactsSlice";
import {
  useGetEmailFoldersQuery,
  useGetEmailsQuery,
  useLazyGetEmailsQuery,
} from "../../../slices/EmailsSlice";
import { useGetNotificationListQuery } from "../../../slices/NotificationCenterSlice";
import { useGetRolesQuery } from "../../../slices/RoleSlice";
import { useGetStatusesQuery } from "../../../slices/StatusesSlice";
import { useGetWorksQuery } from "../../../slices/WorkSlice";
import {
  filterQueuedWorks,
  myRoleIds,
} from "../../works/board/worksBoard.utils";
import { WorkTableSort } from "../../works/types/work-table";
import {
  countCompletedBetween,
  dueOnCount,
  homeRanges,
  HomeView,
  mergeAttention,
  onlyDueOn,
  rankStatuses,
} from "./homeDashboard.utils";

/** Active work loaded to count statuses (more than a firm usually has). */
const PIPELINE_FETCH_SIZE = 2000;
const LIST_SIZE = 8;
/** Needs attention today scrolls, so it can hold more. */
const ATTENTION_SIZE = 25;
/** Rows loaded to count "Due today" by the browser's calendar day (QA DATA-008). */
const DUE_DAY_SIZE = 100;
/** Recent client emails = the inbox's last 30 days (owner 2026-10-23), at most 10 pages of 30. */
export const RECENT_EMAIL_DAYS = 30;
const MAX_INBOX_PAGES = 10;
const NO_MAILBOX = "Grant for this User doesn't exists. Try authorizing again.";

const totalOf = (works?: WorkModel[]) =>
  works ? works[0]?.totalRecords ?? 0 : undefined;

type Range = { fromDate: Date; toDate: Date };

/** How many works match (a page of one, reading totalRecords). */
const useWorkCount = (
  args: Parameters<typeof useGetWorksQuery>[0],
  skip: boolean,
) => totalOf(useGetWorksQuery(args, { skip }).data);

/** Everything the Home page shows, for "My view" or "Firm view". */
export const useHomeDashboard = (view: HomeView) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";
  const skip = !orgId || !user?.id;
  // Recomputed once per page load; good enough for a dashboard.
  const ranges = useMemo(() => homeRanges(), []);

  const base = {
    orgId,
    currentUserId: user?.id,
    userIds: view === "mine" && user?.id ? [user.id] : [],
    hideGreenStatus: true,
    pageNumber: 1,
    pageSize: 1,
    sortBy: WorkTableSort.DueDate,
    sortOrder: "asc" as const,
  };
  const due = (range: Range) => ({ dueDates: ["BetweenDates"], ...range });
  const count = (extra: object) => ({ ...base, ...extra });

  // Pipeline: the most used statuses among active work (counted here; the
  // server only counts the main groups).
  const { data: activeWorks } = useGetWorksQuery(
    { ...base, pageSize: PIPELINE_FETCH_SIZE },
    { skip },
  );
  const { data: statusGroups } = useGetStatusesQuery(
    { orgId },
    { skip: !orgId },
  );
  const pipeline = useMemo(
    () =>
      activeWorks
        ? rankStatuses(
            activeWorks,
            Object.fromEntries(
              (statusGroups ?? []).map((group) => [
                `${group.name}`.toLowerCase(),
                group.color,
              ]),
            ),
          )
        : undefined,
    [activeWorks, statusGroups],
  );
  // Today's work (Due today card + Needs attention): checked by the browser's calendar day.
  const todayList = useGetWorksQuery(
    { ...base, ...due(ranges.dueToday), pageSize: DUE_DAY_SIZE },
    { skip },
  );
  const cards = {
    overdue: useWorkCount(count(due(ranges.overdue)), skip),
    dueToday: dueOnCount(todayList.data, ranges.today),
    dueTodayLast: dueOnCount(
      useGetWorksQuery(
        { ...base, ...due(ranges.dueTodayLast), pageSize: DUE_DAY_SIZE },
        { skip },
      ).data,
      ranges.weekAgo,
    ),
    dueWeek: useWorkCount(count(due(ranges.dueWeek)), skip),
    dueWeekLast: useWorkCount(count(due(ranges.dueWeekLast)), skip),
    active: useWorkCount(count({}), skip),
  };

  // "Completed this week": completed work due around now, by when it was
  // completed (find-work can't filter by completion date).
  const { data: completed } = useGetWorksQuery(
    {
      ...base,
      ...due(ranges.completedLookup),
      workStatuses: ["Completed"],
      hideGreenStatus: false,
      pageSize: 500,
    },
    { skip },
  );
  const completedWeek = countCompletedBetween(
    completed,
    ranges.completedWeek.from,
    ranges.completedWeek.to,
  );
  const completedLastWeek = countCompletedBetween(
    completed,
    ranges.completedLastWeek.from,
    ranges.completedLastWeek.to,
  );

  // Overdue first (most overdue on top), then due today; each list is loaded
  // separately so a long overdue list never hides today's work.
  const overdueList = useGetWorksQuery(
    { ...base, ...due(ranges.overdue), pageSize: ATTENTION_SIZE },
    { skip },
  );
  const attention =
    overdueList.data && todayList.data
      ? mergeAttention(
          overdueList.data,
          onlyDueOn(todayList.data, ranges.today).slice(0, ATTENTION_SIZE),
        )
      : undefined;
  const upcoming = useGetWorksQuery(
    { ...base, ...due(ranges.upcoming), pageSize: LIST_SIZE },
    { skip },
  );

  // The bottom row is always about the signed-in person.
  const { data: roles } = useGetRolesQuery({ orgId }, { skip: !orgId });
  const queued = useGetWorksQuery(
    {
      ...base,
      userIds: [],
      isQueued: true,
      pageSize: 500,
      sortBy: WorkTableSort.DueDate,
    },
    { skip },
  );
  const canPick = useMemo(
    () => filterQueuedWorks(queued.data ?? [], [], myRoleIds(roles, user?.id)),
    [queued.data, roles, user?.id],
  );

  const notificationList = useGetNotificationListQuery(
    { orgId, view: "inbox", take: LIST_SIZE },
    { skip },
  );
  const notifications = notificationList.data ?? [];

  return {
    user,
    pipeline,
    cards: { ...cards, completedWeek, completedLastWeek },
    attention,
    upcoming: upcoming.data,
    canPick,
    isLoadingQueued: queued.isLoading,
    notifications,
    isLoadingNotifications: notificationList.isLoading,
    hasAnyWork: cards.active === undefined ? undefined : cards.active > 0,
  };
};

/** Inbox emails of the last 30 days whose sender is one of the firm's clients. */
export const useClientInbox = () => {
  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";
  const userId = user?.id || "";
  const { data: folders, error: foldersError } = useGetEmailFoldersQuery(
    { userId },
    { skip: !userId },
  );
  const inboxId =
    folders?.find((folder) => folder.name?.toUpperCase() === "INBOX")?.id || "";
  const {
    data: firstPage,
    error,
    isLoading,
  } = useGetEmailsQuery(
    { userId, folderId: inboxId, pageNumber: undefined },
    { skip: !userId || !inboxId },
  );
  const [loadPage] = useLazyGetEmailsQuery();
  const [older, setOlder] = useState<{ from?: unknown; emails: Email[] }>({
    emails: [],
  });
  const cutoff = useMemo(
    () => Date.now() / 1000 - RECENT_EMAIL_DAYS * 24 * 60 * 60,
    [],
  );

  // Older pages, one after another, until an email is older than 30 days.
  // Saved once at the end (one re-render).
  useEffect(() => {
    if (!firstPage) return;
    let cancelled = false;
    const load = async () => {
      const collected: Email[] = [];
      let last = firstPage;
      for (let page = 1; page < MAX_INBOX_PAGES; page += 1) {
        const oldest = Math.min(...last.data.map((email) => email.date));
        if (!last.nextPageValue || !last.data.length || oldest < cutoff) break;
        try {
          // eslint-disable-next-line no-await-in-loop
          last = await loadPage(
            { userId, folderId: inboxId, pageNumber: last.nextPageValue },
            true,
          ).unwrap();
        } catch {
          break;
        }
        if (cancelled) return;
        collected.push(...last.data);
      }
      if (!cancelled) setOlder({ from: firstPage, emails: collected });
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [firstPage, userId, inboxId, loadPage, cutoff]);

  const emails = useMemo(() => {
    const seen = new Set<string>();
    return [...(firstPage?.data ?? []), ...older.emails].filter((email) => {
      if (email.date < cutoff || seen.has(email.id)) return false;
      seen.add(email.id);
      return true;
    });
  }, [firstPage, older, cutoff]);

  const [lookUp] = useLazyGetCustomersByEmailQuery();
  const [clientOf, setClientOf] = useState<Record<string, boolean>>({});
  // Each sender is looked up once. (Re-running on every answer for the senders
  // still pending re-rendered the page ~2^senders times: production freeze 2026-10-23.)
  const asked = useRef(new Set<string>());

  useEffect(() => {
    const senders = [
      ...new Set(
        emails
          .map((email) => email.from?.[0]?.email?.toLowerCase())
          .filter((email): email is string => !!email),
      ),
    ].filter((email) => !asked.current.has(email));
    if (!senders.length) return;
    senders.forEach((email) => asked.current.add(email));
    void Promise.all(
      senders.map((email) =>
        lookUp({ email, orgId }, true)
          .unwrap()
          .then((customers) => [email, !!customers?.length] as const)
          .catch(() => [email, false] as const),
      ),
    ).then((answers) =>
      setClientOf((known) => ({ ...known, ...Object.fromEntries(answers) })),
    );
  }, [emails, orgId, lookUp]);

  const fromClients = emails.filter(
    (email) => clientOf[email.from?.[0]?.email?.toLowerCase() || ""],
  );
  const notConnected =
    !user?.nylasToken ||
    error === NO_MAILBOX ||
    (foldersError as unknown) === NO_MAILBOX;

  return {
    notConnected,
    isLoading:
      isLoading ||
      (!!firstPage && older.from !== firstPage && !fromClients.length) ||
      (!!emails.length && !Object.keys(clientOf).length),
    emails: fromClients,
    unread: fromClients.filter((email) => email.unread).length,
    failed: !!error && !notConnected,
  };
};
